<template>
  <PmwModal
    :value="value"
    :hasFooter="false"
    width="auto"
    smallHeader
    @input="$emit('input', !value)"
  >
    <template #header-content>
      <PmwHeading class="preview-dialog--custom-title"
        >Pré-visualização</PmwHeading
      >
    </template>

    <template #default>
      <div class="preview-dialog-wrapper">
        <!-- SELEÇÃO DE QUARTOS E HÓSPEDES -->
        <b-row>
          <b-col cols="2">
            <FormSelect
              v-model="room"
              :options="roomOptions"
              name="quarto"
              label="Quarto"
              placeholder="Selecione"
            >
              <template #button-label="{ selectedValues }">
                <span
                  v-html="
                    getHighlightedText(
                      '(quarto base)',
                      selectedValues.text,
                      'span'
                    )
                  "
                ></span>
              </template>
            </FormSelect>
          </b-col>

          <b-col v-if="guestOptions.length > 0" cols="2">
            <FormSelect
              v-model="guest"
              :options="paxOptions"
              class="guests-selection"
              name="hospedes"
              label="Hóspedes"
              placeholder="Selecione"
            />
          </b-col>
        </b-row>

        <!-- SELEÇÃO DE PERÍODOS -->
        <b-row>
          <b-col class="dates-filter">
            <Chip
              v-for="(period, i) in datePeriods"
              :key="`period-${i}`"
              :class="{
                chip__selected:
                  JSON.stringify(selectedPeriod) === JSON.stringify(period)
              }"
            >
              <InputRadio
                v-model="selectedPeriod"
                :inputValue="period"
                :label="
                  `${formatDate(
                    new Date(period.rawStart),
                    'dd MMM yyyy'
                  )} - ${formatDate(new Date(period.rawEnd), 'dd MMM yyyy')}`
                "
                :value="period.start"
                :checked="
                  JSON.stringify(selectedPeriod) === JSON.stringify(period) ||
                    i === 0
                "
                name="date-period"
              />
            </Chip>
          </b-col>
        </b-row>

        <!-- CALENDÁRIO DE PRÉ-VISUALIZAÇÃO -->
        <b-row>
          <b-col class="calendar-wrapper">
            <FullCalendar
              :period="selectedPeriod"
              :attributes="attributes"
            ></FullCalendar>
          </b-col>
        </b-row>
      </div>
    </template>
  </PmwModal>
</template>

<script>
/** Componenete da modal de Pré-visualização das tarifas no calendário */
import { parseISO, areIntervalsOverlapping } from 'date-fns'
import { mapGetters } from 'vuex'
import PmwModal from '@solucoes/components-lib-tmp/src/components/patterns/PmwModal'
import FullCalendar from '@/components/hotel/rates/FullCalendar'
import PmwHeading from '@solucoes/components-lib-tmp/src/components/elements/PmwHeading'
import FormSelect from '@/components/_base/elements/FormSelect'
import InputRadio from '@/components/_base/elements/InputRadio'
import Chip from '@/components/_base/patterns/Chip'
import { getHighlightedText } from '@/helpers/string'
import { formatDate, getWeekDaysFromInterval } from '@/helpers/date'

export default {
  name: 'PreviewPointsCalendarDialog',
  components: {
    FullCalendar,
    PmwModal,
    FormSelect,
    InputRadio,
    Chip,
    PmwHeading
  },
  props: {
    /** Valor do estado de visualização da modal */
    value: {
      type: Boolean,
      default: false
    },
    /**
     * Opções de quartos
     * {
     *   text: String,
     *   value: String
     * }
     * */
    roomOptions: {
      type: Array,
      default: () => []
    },
    /**
     * Opções de hóspedes
     * {
     *   text: String,
     *   value: String
     * }
     * */
    guestOptions: {
      type: Array,
      default: () => []
    },
    /**
     * Lista de períodos selecionáveis
     * {
     *    rawStart: Datetime,
     *    start?: String,
     *    rawEnd: Datetime,
     *    end?: String
     * }
     */
    datePeriods: {
      type: Array,
      required: true
    },
    /** Lista de quartos com suas regras */
    roomsRatesRules: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      room: this.roomOptions[0] /** Inicia com o primeiro valor selecionado */,
      guest:
        this.guestOptions[0] ||
        null /** Inicia com o primeiro valor selecionado caso seja absoluta */,
      selectedPeriod: this
        .datePeriods[0] /** Inicia com o primeiro valor selecionado */
    }
  },
  computed: {
    /** Valores formatados para apresentação no calendário */
    attributes() {
      /** Armazena os valores encontrados */
      const finalAttributes = []

      /**
       * Filtra as regras de tarifas verificando
       * se está dentro do período escolhido
       * */
      const filteredRatesByPeriod = this.filterByPeriods(
        this.roomsRatesRules || []
      )

      /** Se possuí regras de valores */
      if (filteredRatesByPeriod.length > 0) {
        /** Busca os quartos dentro da tarifa encontrada, de acordo com o filtro selecionado */
        const selectedRoomsFromPeriod = filteredRatesByPeriod[0].quartos.filter(
          (item) => this.room.value === item.codigo
        )

        /**
         * Para cada regra do período para o quarto
         * monta um obj formatado para o calendário
         *
         * @example {
         *  customData: {
         *     guest: String,
         *     price: String,
         *     detail: String,
         *     closed: Number // 0 === aberto, 1 === fechado, 3 === default
         *   },
         *   dates: [Datetime]
         * }
         */
        if (selectedRoomsFromPeriod.length > 0) {
          /** Filtra os quartos de acordo com os hóspedes selecionados */
          selectedRoomsFromPeriod.forEach((room) => {
            const guest = room.valores.filter((guest) => {
              const type =
                this.guest?.value.match(/[^\d]{1,}/gim)[0] === 'pax' ? 10 : 8
              const quantity = this.guest?.value.match(/[\d]{1,}/gim)[0]

              return (
                type === guest.tipoPax && +quantity === +guest.quantidadePax
              )
            })

            /** Armazena as datas de acordo com os dias da semana selecionados  */
            const weekDays = this.getWeekDaysFromInterval(
              parseISO(filteredRatesByPeriod[0].dataInicial),
              parseISO(filteredRatesByPeriod[0].dataFinal),
              room.diasSemana
            )

            /**
             * Extraí as datas dos dias da semana e gera um obj para cada dia
             * para facilitar a comparação com as restrições
             * */
            weekDays.forEach((day) => {
              if (!guest[0]) return

              const dayObj = {
                customData: {
                  guest: this.guest?.text,
                  price: `${guest[0]?.quantidadePontos} Pontos `,
                  closed: 3
                },
                dates: day
              }

              finalAttributes.push(dayObj)
            })
          })
        }

        /** Retorna o valor encontrado e finaliza a execução */
        return finalAttributes
      }

      /**
       * TODO Na V1 só mostraremos o calendário quando houver atualização de tarifas
       * TODO Ver com a FÊ qual seria o comportamento da modal quando houver somente restrições
       */

      return finalAttributes
    },
    /** Retorna as opções de hóspedes de acordo com a acomodação selecionada */
    paxOptions() {
      const options = this.guestOptions.filter((item) =>
        item.rooms.includes(this.room?.value)
      )

      return options
    },
    ...mapGetters('hotel', ['getCurrency'])
  },
  watch: {
    /**
     * Escuta as mudanças nas seleções de datas
     * para setar o valor inicial ao abrir o calendário
     * */
    datePeriods(newValue) {
      if (newValue) {
        this.selectedPeriod = newValue[0]
      }
    },
    /**
     * Escuta as mudanças nas seleções de quartos
     * para setar o valor inicial ao abrir o calendário
     * */
    roomOptions(newValue) {
      if (newValue) {
        this.room = newValue[0]
      }
    },
    /**
     * Escuta as mudanças nas seleções de hóspedes
     * para setar o valor inicial ao abrir o calendário
     * */
    guestOptions(newValue) {
      if (newValue) {
        this.guest = newValue[0]
      }
    },
    /**
     * Escuta as mudanças de quartos para selecionar a primeira
     * opção de hóspedes para o quarto selecionado
     * */
    room() {
      this.guest = this.paxOptions[0]
    }
  },
  methods: {
    formatDate: formatDate,
    /**
     * Retorna os dias da semana dentro de um período de tempo
     * @param {Date} start - data inicial
     * @param {Date} end - data final
     * @param {Array} days - array com os dias da semana
     * @example getWeekDaysFromInterval(new Date(2021,5,15), new Date(2021,5,25), [2,3,4])
     * @returns {Array}
     */
    getWeekDaysFromInterval(start, end, days) {
      return getWeekDaysFromInterval(
        {
          start: new Date(start).setHours(0, 0, 0, 0),
          end: new Date(end).setHours(0, 0, 0, 0)
        },
        days.flatMap((day) => +day - 1 /** Padroniza os dias para o date-fns */)
      )
    },
    /**
     * Filtra os itens pelos períodos de tempo. Se o período dos itens passa
     * pelo período selecionado, considera-se um período válido.
     * @param {Array} items - array de objetos que possui um atributo dataInicial e um dataFinal na raiz
     * @returns {Array} - Array de ocorrências
     */
    filterByPeriods(items) {
      if (!this.selectedPeriod) return []

      return items.filter((item) =>
        areIntervalsOverlapping(
          { start: parseISO(item.dataInicial), end: parseISO(item.dataFinal) },
          {
            start: parseISO(this.selectedPeriod.start),
            end: parseISO(this.selectedPeriod.end)
          },
          {
            inclusive: true
          }
        )
      )
    },
    getHighlightedText: getHighlightedText
  }
}
</script>

<style lang="scss" scoped>
.guests-selection {
  margin-left: 20px;
  &::v-deep {
    .label,
    .select-option button {
      text-transform: capitalize;
    }
  }
}

.preview-dialog-wrapper {
  padding: 38px 40px 40px;
}

.dates-filter {
  display: flex;
  align-items: center;
  margin-top: 25px;
}

.calendar-wrapper {
  margin-top: 25px;
  max-height: 70vh;
  overflow-y: visible;
  @include scrollbars(5px, $gray-border, #fff);
}

.preview-dialog--custom-title {
  margin: 0;
}

.chip {
  &__selected {
    border-color: $primary;
  }
}

::v-deep .select-options--option {
  span {
    color: $gray-dark;
  }
}

::v-deep .label__selected {
  span span {
    color: $gray-dark;
  }
}
</style>
